"use client";

import { motion, useAnimation, useReducedMotion, Variants } from "framer-motion";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";

export interface BookmarkXIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface BookmarkXIconProps {
  size?: number;
  duration?: number;
  isAnimated?: boolean;
  isHovered?: boolean;
  className?: string;
}

const BookmarkXIcon = forwardRef<BookmarkXIconHandle, BookmarkXIconProps>(
  (
    {
      className,
      size = 24,
      duration = 1,
      isAnimated = true,
      isHovered = false,
    },
    ref,
  ) => {
    const controls = useAnimation();
    const reduced = useReducedMotion();
    const isControlled = useRef(false);
    const prevHovered = useRef(isHovered);

    useImperativeHandle(ref, () => {
      isControlled.current = true;
      return {
        startAnimation: () =>
          reduced ? controls.start("normal") : controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    // Animate on mount if isHovered is true
    useEffect(() => {
      if (isHovered && isAnimated && !reduced) {
        controls.start("animate");
      }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // React to isHovered prop changes
    if (isHovered !== prevHovered.current) {
      prevHovered.current = isHovered;
      if (isHovered && isAnimated && !reduced) {
        controls.start("animate");
      } else if (!isHovered) {
        controls.start("normal");
      }
    }

    const handleEnter = useCallback(() => {
      if (!isAnimated || reduced) return;
      if (!isControlled.current) {
        controls.start("animate");
      }
    }, [controls, reduced, isAnimated]);

    const handleLeave = useCallback(() => {
      if (!isControlled.current) {
        controls.start("normal");
      }
    }, [controls]);

    const bookmarkVariants: Variants = {
      normal: { rotate: 0, scale: 1 },
      animate: {
        rotate: [0, -4, 4, -2, 0],
        scale: [1, 1.05, 0.95, 1],
        transition: { duration: 1 * duration, ease: "easeInOut" },
      },
    };

    const xLineVariants: Variants = {
      normal: { pathLength: 1, opacity: 1 },
      animate: {
        pathLength: [0, 1],
        opacity: [0.8, 1],
        transition: { duration: 0.6 * duration, ease: "easeInOut" },
      },
    };

    return (
      <motion.div
        className={`relative inline-flex ${className || ""}`}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{ flexShrink: 0 }}
      >
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          animate={controls}
          initial="normal"
          variants={bookmarkVariants}
        >
          <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2Z" />
          <motion.path d="m14.5 7.5-5 5" variants={xLineVariants} />
          <motion.path d="m9.5 7.5 5 5" variants={xLineVariants} />
        </motion.svg>
      </motion.div>
    );
  },
);

BookmarkXIcon.displayName = "BookmarkXIcon";
export { BookmarkXIcon };
